import React, { useState, useEffect } from 'react';
import { 
  Map, 
  Trash2, 
  Settings, 
  MapPin, 
  X,
  Info,
  Clock,
  RefreshCw,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import { mapCacheService, type LocalityPoint, type AddressData, type AddressEntry } from '@/services/maps/MapCacheService';
import { offlineStorageService } from '@/services/offline/OfflineStorageService';
import { contactsService, type ContactPayload } from '@/services/contacts/ContactsService';
import { teamsService } from '@/services/teams/TeamsService';
import { useAuthStore } from '@/stores';
import MapCacheSettings from './MapCacheSettings';
import MapLocationSelector from './MapLocationSelector';
import VisitedContactsMap from './VisitedContactsMap';

interface UnifiedMapCacheProps {
  isOpen: boolean;
  onClose: () => void;
}

const UnifiedMapCache: React.FC<UnifiedMapCacheProps> = ({ isOpen, onClose }) => {
  const [cacheStatus, setCacheStatus] = useState(mapCacheService.getCacheStatus());
  const [localities, setLocalities] = useState<LocalityPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [showVisitedMap, setShowVisitedMap] = useState(false);
  const [, setPreloadingAddresses] = useState(false);
  // Addresses state (read-only list)
  const [addresses, setAddresses] = useState<AddressEntry[]>([]);
  const [addressSearch, setAddressSearch] = useState('');
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      updateCacheData();
    }
  }, [isOpen]);

  const updateCacheData = () => {
    setCacheStatus(mapCacheService.getCacheStatus());
    setLocalities(mapCacheService.getCachedLocalities());
    // Load addresses list (read-only)
    setAddresses(mapCacheService.getCachedAddresses());
  };

  // Handle search locally using service search to leverage tokenization/terms
  useEffect(() => {
    if (!isOpen) return;
    if (!addressSearch || addressSearch.trim().length < 2) {
      setAddresses(mapCacheService.getCachedAddresses());
    } else {
      setAddresses(mapCacheService.searchCachedAddresses(addressSearch.trim()));
    }
  }, [addressSearch, isOpen]);

  // removed: handleCacheCurrentLocation (no longer used)

  // manual preload function removed; preloading happens automatically after selecting a map point

  const handleClearCache = () => {
    if (confirm('Limpar todo o cache? Esta ação não pode ser desfeita.')) {
      mapCacheService.clearAllCache();
      updateCacheData();
      alert('Cache limpo com sucesso!');
    }
  };

  const handleRemoveLocality = (id: string) => {
    if (confirm('Remover este ponto do cache?')) {
      mapCacheService.removeLocality(id);
      updateCacheData();
    }
  };

  const handleSyncPending = async () => {
    if (!navigator.onLine) {
      alert('Sem conexão. Conecte-se à internet para sincronizar.');
      return;
    }
    const auth = useAuthStore.getState();
    const userRegionId = auth.user?.region_id ?? null;
    const visits = offlineStorageService.getOfflineVisits();
    const pending = visits.filter(v => !v.synced);
    if (pending.length === 0) {
      alert('Nenhum item pendente para sincronizar.');
      return;
    }
    setSyncing(true);
    let success = 0;
    let failed = 0;
    let skipped = 0;
    const failureMessages: string[] = [];

    const onlyDigits = (s: string) => (s || '').replace(/\D/g, '');
    const normalizePhoneBR = (raw: string) => {
      let d = onlyDigits(raw);
      // Strip leading country code 55 if number becomes longer than 11
      if (d.startsWith('55') && d.length > 11) d = d.slice(2);
      return d;
    };

    // Preload a default team/region (prefer user's region; fallback to first available team globally)
    let defaultTeamId: number | null = null;
    let defaultRegionId: number | null = userRegionId;
    try {
      if (userRegionId) {
        const resp = await teamsService.list({ region_id: userRegionId, per_page: 100 });
        if (resp.data && resp.data.length > 0) {
          defaultTeamId = resp.data[0].id;
          defaultRegionId = resp.data[0].region_id;
        }
      }
      if (!defaultTeamId) {
        const anyTeams = await teamsService.list({ per_page: 100 });
        if (anyTeams.data && anyTeams.data.length > 0) {
          defaultTeamId = anyTeams.data[0].id;
          defaultRegionId = anyTeams.data[0].region_id;
        }
      }
    } catch (err) {
      console.warn('Falha ao carregar times para sync:', err);
    }

    for (const v of pending as Array<Record<string, unknown>>) {
      try {
        const type = String(v.type || '');
        const statusMap: Record<string, 'potential' | 'absent' | 'refused'> = {
          person_met: 'potential',
          not_home: 'absent',
          refused: 'refused',
        };
        const status = statusMap[type] ?? 'potential';

        // Name/phone rules:
        // - person_met requires name/phone; otherwise skip
        // - others: derive a name from location; phone optional/null
        let name = String(v.name || '').trim();
        const phoneMasked = String(v.phone || '');
        const phone = normalizePhoneBR(phoneMasked);
        if (type !== 'person_met') {
          const addressDerived = [v.street, v.number, v.neighborhood, v.city, v.state]
            .map(x => (x == null ? '' : String(x)))
            .filter(Boolean)
            .join(', ');
          const locName = String(v.locationName || '');
          if (!name) name = locName || addressDerived || 'Visita de Campo';
        }
        if (type === 'person_met') {
          if (!name || !phone) { skipped++; continue; }
        }

        const addressParts = [v.street, v.number, v.neighborhood, v.city, v.state]
          .map(x => (x == null ? '' : String(x)))
          .filter(Boolean);
        const address = addressParts.join(', ') || String(v.locationName || '') || null;
        const notes = (v.observations ? String(v.observations) : null) as string | null;

        // Prefer ids saved with the visit; otherwise, fallback to user region and default team
        const visitRegionId = v.region_id != null ? Number(v.region_id) : null;
        const visitTeamId = v.team_id != null ? Number(v.team_id) : null;
        const effectiveRegionId = visitRegionId ?? defaultRegionId ?? null;
        const effectiveTeamId = visitTeamId ?? defaultTeamId ?? null;

        // If region/team are missing, proceed with nulls (backend accepts nulls for these fields).
        // Keep a warning log to aid diagnosing account setup, but do not fail the sync.
        if (!effectiveRegionId || !effectiveTeamId) {
          const msg = 'Region/Team ausentes: prosseguindo com null (sem falhar). Configure region_id e equipes para melhor classificação.';
          console.warn(msg, { visitId: v.id, userRegionId, defaultTeamId, visitRegionId, visitTeamId });
        }

        const payload: ContactPayload = {
          name,
          email: (v.email ? String(v.email) : null),
          phone: type === 'person_met' ? (phone || null) : null,
          address,
          status,
          notes,
          team_id: effectiveTeamId,
          region_id: effectiveRegionId,
        };

        await contactsService.create({
          name: payload.name!,
          email: payload.email ?? null,
          status: payload.status!,
          team_id: payload.team_id ?? null,
          region_id: payload.region_id ?? null,
          phone: payload.phone ?? null,
          address: payload.address ?? null,
          notes: payload.notes ?? null,
        });

        offlineStorageService.markVisitAsSynced(String(v.id));
        success++;
      } catch (e) {
        let message = 'Erro desconhecido';
        if (e instanceof Error) message = e.message;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const maybe = e as any;
        if (maybe?.response?.data) {
          const data = maybe.response.data;
          if (typeof data === 'string') {
            message = data;
          } else {
            try {
              message = JSON.stringify(data);
            } catch {
              // ignore stringify errors
            }
          }
        }
        console.warn('Falha ao sincronizar item offline:', message);
        failureMessages.push(message);
        failed++;
      }
    }

    // Cleanup and update
    offlineStorageService.removeSyncedVisits();
    updateCacheData();
    setSyncing(false);

    const extra = failed > 0 && failureMessages.length > 0 ? `\nExemplo de erro: ${failureMessages[0]}` : '';
    alert(`Sincronização finalizada. Sucesso: ${success}. Falhas: ${failed}. Ignorados: ${skipped}.` + extra);
  };
  
  const handleRenewExpiration = (id: string) => {
    const updatedLocality = mapCacheService.renewLocalityExpiration(id);
    if (updatedLocality) {
      updateCacheData();
      alert(`Expiração de "${updatedLocality.name}" renovada para ${mapCacheService.formatDate(updatedLocality.expiresAt)}`);
    } else {
      alert('Não foi possível renovar a expiração desta localidade.');
    }
  };

  const handleLocationSelected = (lat: number, lng: number, address: AddressData | null) => {
    setShowLocationSelector(false);
    
    // Prompt for a name
    const name = prompt('Nome para este ponto de localidade:', `Localidade ${localities.length + 1}`);
    if (!name) return;
    
    setIsLoading(true);
    
    // Add the locality with address data if available
    mapCacheService.addLocality(lat, lng, name, address)
      .then(async () => {
        updateCacheData();
        alert(`Localidade "${name}" adicionada ao cache!`);
        // Automatically preload addresses for this selected area (no extra confirmation)
        if (navigator.onLine) {
          setPreloadingAddresses(true);
          try {
            const radiusKm = mapCacheService.getSettings().cacheRadius;
            const cached = await mapCacheService.preloadAreaAddresses(lat, lng, radiusKm);
            alert(`${cached} endereços foram pré-carregados para uso offline!`);
            updateCacheData();
          } catch (e: unknown) {
            const message = e instanceof Error ? e.message : String(e);
            alert('Erro ao pré-carregar endereços: ' + message);
          } finally {
            setPreloadingAddresses(false);
          }
        }
      })
      .catch(error => {
        alert('Erro ao adicionar localidade: ' + error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Cache de Mapas">
        <div className="space-y-6">
          {/* Status Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-2xl font-bold text-blue-600">{cacheStatus.cachedPointsCount}</p>
              <p className="text-xs text-blue-600">Localidades</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="text-2xl font-bold text-green-600">{cacheStatus.cachedAddressesCount}</p>
              <p className="text-xs text-green-600">Endereços</p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100">
              <p className="text-2xl font-bold text-orange-600">{cacheStatus.totalVisits}</p>
              <p className="text-xs text-orange-600">Visitas Salvas</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg border border-red-100">
              <p className="text-2xl font-bold text-red-600">{cacheStatus.pendingSync}</p>
              <p className="text-xs text-red-600">Pendente Sync</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-2xl font-bold text-purple-600">
                {cacheStatus.hasMapCache ? 'Ativo' : 'Inativo'}
              </p>
              <p className="text-xs text-purple-600">Cache Offline</p>
            </div>
          </div>

          {/* Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-medium text-gray-900">Ações de Cache</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Button
                  onClick={() => setShowLocationSelector(true)}
                  disabled={isLoading}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Selecionar no Mapa
                </Button>
                <Button
                  onClick={() => setShowVisitedMap(true)}
                >
                  <Map className="w-4 h-4 mr-2" />
                  Contatos Visitados no Mapa
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="secondary"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configurações
                </Button>
                <Button
                  onClick={handleSyncPending}
                  variant="secondary"
                  disabled={syncing || cacheStatus.pendingSync === 0}
                  title={navigator.onLine ? '' : 'Sem conexão'}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {syncing ? 'Sincronizando...' : 'Sincronizar Pendentes'}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Cached Addresses (read-only) */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-col sm:flex-row">
                <h3 className="text-lg font-medium text-gray-900">Endereços em Cache</h3>
                <input
                  type="text"
                  value={addressSearch}
                  onChange={(e) => setAddressSearch(e.target.value)}
                  placeholder="Buscar (rua, número, bairro, cidade)"
                  className="w-full sm:w-80 border border-gray-300 rounded-md shadow-sm py-1.5 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </CardHeader>
            <CardBody>
              {addresses.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {addresses.map(addr => (
                    <div key={addr.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-900">
                        {(addr.address.road || '')}
                        {addr.address.house_number ? `, ${addr.address.house_number}` : ''}
                      </div>
                      <div className="text-xs text-gray-600">
                        {[addr.address.suburb, addr.address.city, addr.address.state]
                          .filter(Boolean)
                          .join(' • ')}
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500">
                        {addr.lat.toFixed(5)}, {addr.lng.toFixed(5)} • Cacheado em {mapCacheService.formatDate(addr.cachedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Map className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum endereço em cache{addressSearch ? ' para esta busca' : ''}</p>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Localities */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-medium text-gray-900">Localidades em Cache</h3>
            </CardHeader>
            <CardBody>
              {localities.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {localities.map(locality => (
                    <div key={locality.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-2">
                      <div className="flex-1 sm:pr-2">
                        <div className="font-medium text-gray-900">{locality.name}</div>
                        <div className="text-xs sm:text-sm text-gray-600">
                          {locality.lat.toFixed(4)}, {locality.lng.toFixed(4)} • {locality.radius}km
                        </div>
                        <div className="text-xs text-gray-500 flex items-center flex-wrap">
                          <Info className="w-3 h-3 inline mr-1" />
                          Expira em: {mapCacheService.formatDate(locality.expiresAt)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 justify-end sm:justify-start">
                        <button
                          onClick={() => handleRenewExpiration(locality.id)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Renovar expiração"
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemoveLocality(locality.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Remover localidade"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Map className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma localidade em cache</p>
                  <p className="text-sm">Adicione locais para usar offline</p>
                </div>
              )}

              {localities.length > 0 && (
                <div className="mt-4">
                  <Button
                    onClick={handleClearCache}
                    variant="danger"
                    className="w-full"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Limpar Todo o Cache
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Footer */}
          <div className="flex justify-center sm:justify-end mt-4">
            <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto">
              Fechar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Settings Modal */}
      <MapCacheSettings 
        isOpen={showSettings} 
        onClose={() => {
          setShowSettings(false);
          updateCacheData();
        }} 
      />

      {/* Map Location Selector */}
      <MapLocationSelector
        isOpen={showLocationSelector}
        onClose={() => setShowLocationSelector(false)}
        onLocationSelected={handleLocationSelected}
      />

      {/* Visited Contacts Map */}
      <VisitedContactsMap
        isOpen={showVisitedMap}
        onClose={() => setShowVisitedMap(false)}
      />
    </>
  );
};

export default UnifiedMapCache;
